import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, PlusIcon, PencilSquareIcon, ArchiveBoxIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const ROLES = [
  { value: 'director', label: 'Director' },
  { value: 'admin', label: 'Office Admin' },
  { value: 'teacher', label: 'Full-Time Teacher' },
  { value: 'float', label: 'Part-Time Float' },
  { value: 'cook', label: 'Cook' },
];

export default function CompensationTiersPage() {
  const [role, setRole] = useState('teacher');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comp-tiers?role=${role}`);
      setTiers(data);
    } catch { toast.error('Failed to load comp tiers'); }
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const archive = async (id) => {
    if (!confirm('Archive this tier? Staff currently in this tier will fall to the next-lower one.')) return;
    try {
      await api.delete(`/comp-tiers/${id}`);
      toast.success('Archived');
      load();
    } catch { toast.error('Archive failed'); }
  };

  return (
    <div className="animate-slide-up">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BanknotesIcon className="w-6 h-6" /> Compensation Tiers
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Map an average goal score to a raise %. Staff land in the highest tier their avg meets.
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
          <PlusIcon className="w-4 h-4" /> New tier
        </button>
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {ROLES.map(r => (
          <button key={r.value} onClick={() => setRole(r.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${role === r.value ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : tiers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No tiers for this role yet. Click "New tier" to add one.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase bg-gray-50">
                <th className="text-left px-5 py-3">Min avg score</th>
                <th className="text-left px-5 py-3">Tier label</th>
                <th className="text-left px-5 py-3">Raise %</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tiers.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-semibold">{Number(t.min_avg_score).toFixed(1)}</td>
                  <td className="px-5 py-3">{t.label}</td>
                  <td className="px-5 py-3 text-orange-600 font-medium">+{Number(t.raise_percent).toFixed(2)}%</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setEditing(t)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 mr-1" title="Edit">
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button onClick={() => archive(t.id)}
                      className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600" title="Archive">
                      <ArchiveBoxIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TierModal
          tier={editing === 'new' ? { role } : editing}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TierModal({ tier, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    role: tier.role || 'teacher',
    label: tier.label || '',
    min_avg_score: tier.min_avg_score != null ? String(tier.min_avg_score) : '',
    raise_percent: tier.raise_percent != null ? String(tier.raise_percent) : '',
    display_order: tier.display_order != null ? String(tier.display_order) : '0',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.label || form.min_avg_score === '' || form.raise_percent === '') {
      toast.error('Label, min avg score, and raise % are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        role: form.role,
        label: form.label.trim(),
        min_avg_score: Number(form.min_avg_score),
        raise_percent: Number(form.raise_percent),
        display_order: Number(form.display_order) || 0,
      };
      if (isNew) {
        await api.post('/comp-tiers', payload);
        toast.success('Tier created');
      } else {
        await api.patch(`/comp-tiers/${tier.id}`, payload);
        toast.success('Tier updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold">{isNew ? 'New compensation tier' : 'Edit tier'}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Tier label">
            <input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})}
              placeholder='e.g. "Top performer", "Meets expectations"'
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Minimum average score to qualify">
            <input type="number" step="0.1" value={form.min_avg_score}
              onChange={(e) => setForm({...form, min_avg_score: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Raise % at this tier">
            <input type="number" step="0.25" value={form.raise_percent}
              onChange={(e) => setForm({...form, raise_percent: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Display order">
            <input type="number" value={form.display_order}
              onChange={(e) => setForm({...form, display_order: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
            <button onClick={save} disabled={saving}
              className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
